import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, PartyPopper, Plus, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import {
  JOB_CATEGORIES,
  ADMINISTRATION_TYPES,
  GALA_SPONSORS,
  SPECIAL_AWARD_CATEGORY,
  SPECIAL_AWARDS,
} from '../constants/registration';

type Tipo = 'finalista' | 'invitado_novagob' | 'invitado_madrid_talento' | 'invitado_patrocinador';

const TIPO_OPTIONS: { value: Tipo; label: string }[] = [
  { value: 'finalista', label: 'Soy finalista' },
  { value: 'invitado_novagob', label: 'Invitado/a de NovaGob' },
  { value: 'invitado_madrid_talento', label: 'Invitado/a de Madrid Talento' },
  { value: 'invitado_patrocinador', label: 'Invitado/a de un patrocinador' },
];

interface Companion {
  nombre: string;
  apellidos: string;
  email: string;
}

const emptyCompanion = (): Companion => ({ nombre: '', apellidos: '', email: '' });

const GalaRegistration: React.FC = () => {
  // Esta página no se enlaza desde ningún sitio del site; nos aseguramos
  // además de que los buscadores no la indexen mientras esté montada.
  useEffect(() => {
    const robots = document.head.querySelector('meta[name="robots"]');
    const prev = robots?.getAttribute('content') || null;
    robots?.setAttribute('content', 'noindex, nofollow');
    return () => {
      if (robots && prev) robots.setAttribute('content', prev);
    };
  }, []);

  const [tipo, setTipo] = useState<Tipo | null>(null);
  const [nombre, setNombre] = useState('');
  const [apellidos, setApellidos] = useState('');
  const [email, setEmail] = useState('');
  const [telefono, setTelefono] = useState('');
  const [cargo, setCargo] = useState('');
  const [institucion, setInstitucion] = useState('');
  const [categoriaPuesto, setCategoriaPuesto] = useState('');
  const [tipoAdministracion, setTipoAdministracion] = useState('');
  const [companions, setCompanions] = useState<Companion[]>([]);
  const [categoriaPremio, setCategoriaPremio] = useState('');
  const [patrocinador, setPatrocinador] = useState('');
  const [premioEspecial, setPremioEspecial] = useState('');
  const [premioCategories, setPremioCategories] = useState<string[]>([]);

  useEffect(() => {
    supabase
      .schema('premios')
      .from('categories')
      .select('name')
      .order('name')
      .then(({ data }) => setPremioCategories((data || []).map((c: { name: string }) => c.name)));
  }, []);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const isFinalista = tipo === 'finalista';

  const updateCompanion = (i: number, field: keyof Companion, value: string) => {
    setCompanions((cs) => cs.map((c, idx) => (idx === i ? { ...c, [field]: value } : c)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tipo) return;
    setError('');
    setLoading(true);

    try {
      // Generamos el id en el cliente (en vez de pedirlo de vuelta con
      // .select() tras el INSERT) porque la tabla no es legible por quien
      // rellena el formulario (rol anon/authenticated sin ser equipo
      // NovaGob) — solo puede insertar, nunca leer datos de otras personas.
      const registrationId =
        typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

      const { error: insertError } = await supabase
        .schema('premios')
        .from('gala_registrations')
        .insert([
          {
            id: registrationId,
            tipo,
            nombre: nombre.trim(),
            apellidos: apellidos.trim(),
            email: email.trim(),
            telefono: isFinalista ? telefono.trim() : null,
            cargo: cargo.trim() || null,
            institucion: institucion.trim() || null,
            categoria_puesto: isFinalista ? null : categoriaPuesto || null,
            tipo_administracion: isFinalista ? null : tipoAdministracion || null,
            categoria_premio: isFinalista ? categoriaPremio || null : null,
            premio_especial: isFinalista && categoriaPremio === SPECIAL_AWARD_CATEGORY ? premioEspecial || null : null,
            patrocinador: tipo === 'invitado_patrocinador' ? patrocinador || null : null,
          },
        ]);

      if (insertError) throw insertError;

      const validCompanions = isFinalista
        ? companions.filter((c) => c.nombre.trim() && c.apellidos.trim() && c.email.trim())
        : [];

      if (validCompanions.length > 0) {
        const { error: companionsError } = await supabase
          .schema('premios')
          .from('gala_companions')
          .insert(
            validCompanions.map((c) => ({
              registration_id: registrationId,
              nombre: c.nombre.trim(),
              apellidos: c.apellidos.trim(),
              email: c.email.trim(),
            }))
          );
        if (companionsError) throw companionsError;
      }

      // El email de confirmación es "best effort": si falla, no bloqueamos
      // la inscripción, que ya ha quedado guardada en la base de datos.
      supabase.functions
        .invoke('send-gala-confirmation', { body: { registration_id: registrationId } })
        .catch((err) => console.error('Error al enviar email de confirmación de gala:', err));

      setDone(true);
    } catch (err) {
      console.error('Error al registrar inscripción a la gala:', err);
      setError('No se ha podido completar la inscripción. Inténtalo de nuevo en unos instantes.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    const celebrationTitle =
      tipo === 'finalista' ? '¡Enhorabuena por llegar hasta la Gala!' : '¡Todo listo para la Gala!';

    return (
      <div className="informe-page">
        <style>{`
          @keyframes galaPop {
            0% { opacity: 0; transform: scale(0.7); }
            60% { opacity: 1; transform: scale(1.08); }
            100% { opacity: 1; transform: scale(1); }
          }
          @keyframes galaRingPulse {
            0% { transform: scale(0.8); opacity: 0.6; }
            100% { transform: scale(2.1); opacity: 0; }
          }
          @keyframes galaFadeUp {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes galaConfetti {
            0% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
            100% { transform: translate(var(--gala-tx), var(--gala-ty)) rotate(var(--gala-rot)); opacity: 0; }
          }
          .gala-success-icon-wrap { position: relative; display: inline-flex; align-items: center; justify-content: center; }
          .gala-success-ring {
            position: absolute;
            width: 64px;
            height: 64px;
            border-radius: 999px;
            border: 2px solid #ffd702;
            animation: galaRingPulse 1.6s ease-out infinite;
          }
          .gala-success-ring-delay { animation-delay: 0.5s; }
          .gala-success-icon {
            position: relative;
            z-index: 1;
            animation: galaPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both;
          }
          .gala-success-title {
            animation: galaFadeUp 0.5s ease 0.15s both;
          }
          .gala-success-subtitle {
            animation: galaFadeUp 0.5s ease 0.3s both;
          }
          .gala-confetti-dot {
            position: absolute;
            top: 50%;
            left: 50%;
            width: 8px;
            height: 8px;
            border-radius: 2px;
            animation: galaConfetti 0.9s ease-out both;
          }
        `}</style>

        <div
          className="informe-page-content"
          style={{ textAlign: 'center', paddingTop: 100, maxWidth: 520 }}
        >
          <div className="gala-success-icon-wrap" style={{ width: 64, height: 64, marginBottom: 20 }}>
            <span className="gala-success-ring" />
            <span className="gala-success-ring gala-success-ring-delay" />
            {[
              { tx: '-36px', ty: '-40px', rot: '120deg', color: '#ffd702', delay: '0.05s' },
              { tx: '34px', ty: '-38px', rot: '-100deg', color: '#ffffff', delay: '0.1s' },
              { tx: '-44px', ty: '16px', rot: '60deg', color: '#ffffff', delay: '0.15s' },
              { tx: '42px', ty: '20px', rot: '-60deg', color: '#ffd702', delay: '0.08s' },
              { tx: '0px', ty: '-52px', rot: '45deg', color: '#ffd702', delay: '0.2s' },
              { tx: '0px', ty: '46px', rot: '-45deg', color: '#ffffff', delay: '0.12s' },
            ].map((c, i) => (
              <span
                key={i}
                className="gala-confetti-dot"
                style={
                  {
                    backgroundColor: c.color,
                    animationDelay: c.delay,
                    '--gala-tx': c.tx,
                    '--gala-ty': c.ty,
                    '--gala-rot': c.rot,
                  } as React.CSSProperties
                }
              />
            ))}
            <PartyPopper className="gala-success-icon" size={48} color="#ffd702" />
          </div>

          <h2 className="gala-success-title" style={{ margin: '0 0 10px' }}>
            {celebrationTitle}
          </h2>
          <p className="gala-success-subtitle" style={{ color: 'var(--color-text-secondary)' }}>
            Tu inscripción está confirmada. Te hemos enviado un email con todos los detalles.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="informe-page">
      <header className="informe-page-header">
        <Link to="/" className="informe-page-back">
          <ArrowLeft size={18} /> Volver
        </Link>
        <span className="informe-page-title">Inscripción Gala 2026</span>
        <span />
      </header>

      <div className="informe-page-content" style={{ maxWidth: 560 }}>
        <section className="bases-modal-section">
          <h3>Gala de los Premios NovaGob Excelencia 2026</h3>
          <p>
            10 de diciembre de 2026 · Casa de la Panadería, Calle Arco del Triunfo, 3, Centro, 28012 Madrid.
            Rellena este formulario para confirmar tu asistencia.
          </p>
        </section>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">¿Quién eres?</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
              {TIPO_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setTipo(opt.value)}
                  className="btn"
                  style={{
                    textAlign: 'left',
                    background: tipo === opt.value ? 'rgba(255,215,2,0.14)' : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${tipo === opt.value ? 'rgba(255,215,2,0.6)' : 'rgba(255,255,255,0.12)'}`,
                    color: tipo === opt.value ? '#ffd702' : 'inherit',
                    fontWeight: tipo === opt.value ? 700 : 500,
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {tipo && (
            <>
              <div className="form-group">
                <label className="form-label">Nombre</label>
                <input
                  type="text"
                  className="form-input"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Apellidos</label>
                <input
                  type="text"
                  className="form-input"
                  value={apellidos}
                  onChange={(e) => setApellidos(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              {isFinalista && (
                <div className="form-group">
                  <label className="form-label">Teléfono de contacto</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={telefono}
                    onChange={(e) => setTelefono(e.target.value)}
                    required
                    placeholder="Por si necesitamos avisarte de algo el día de la Gala"
                  />
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Cargo / Puesto</label>
                <input
                  type="text"
                  className="form-input"
                  value={cargo}
                  onChange={(e) => setCargo(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Institución / Organización / Empresa</label>
                <input
                  type="text"
                  className="form-input"
                  value={institucion}
                  onChange={(e) => setInstitucion(e.target.value)}
                  required
                />
              </div>

              {isFinalista && (
                <div className="form-group">
                  <label className="form-label">Categoría del premio</label>
                  <select
                    className="form-input"
                    value={categoriaPremio}
                    onChange={(e) => setCategoriaPremio(e.target.value)}
                    required
                  >
                    <option value="">Selecciona tu categoría</option>
                    {[...premioCategories, SPECIAL_AWARD_CATEGORY].map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {isFinalista && categoriaPremio === SPECIAL_AWARD_CATEGORY && (
                <div className="form-group">
                  <label className="form-label">¿Qué premio especial?</label>
                  <select
                    className="form-input"
                    value={premioEspecial}
                    onChange={(e) => setPremioEspecial(e.target.value)}
                    required
                  >
                    <option value="">Selecciona el premio especial</option>
                    {SPECIAL_AWARDS.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {tipo === 'invitado_patrocinador' && (
                <div className="form-group">
                  <label className="form-label">Patrocinador</label>
                  <select
                    className="form-input"
                    value={patrocinador}
                    onChange={(e) => setPatrocinador(e.target.value)}
                    required
                  >
                    <option value="">Selecciona el patrocinador</option>
                    {GALA_SPONSORS.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {!isFinalista && (
                <>
                  <div className="form-group">
                    <label className="form-label">Categoría de puesto</label>
                    <select
                      className="form-input"
                      value={categoriaPuesto}
                      onChange={(e) => setCategoriaPuesto(e.target.value)}
                      required
                    >
                      <option value="">Selecciona una categoría</option>
                      {JOB_CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Tipo de Administración</label>
                    <select
                      className="form-input"
                      value={tipoAdministracion}
                      onChange={(e) => setTipoAdministracion(e.target.value)}
                      required
                    >
                      <option value="">Selecciona tipo de administración</option>
                      {ADMINISTRATION_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {isFinalista && (
                <div className="form-group">
                  <label className="form-label">Acompañantes (máximo 2, opcional)</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 8 }}>
                    {companions.map((c, i) => (
                      <div
                        key={i}
                        style={{
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: 10,
                          padding: 14,
                          position: 'relative',
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => setCompanions((cs) => cs.filter((_, idx) => idx !== i))}
                          style={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            background: 'none',
                            border: 'none',
                            color: 'var(--color-text-secondary)',
                            cursor: 'pointer',
                          }}
                          aria-label="Quitar acompañante"
                        >
                          <X size={16} />
                        </button>
                        <p style={{ margin: '0 0 10px', fontWeight: 700, fontSize: 13 }}>
                          Acompañante {i + 1}
                        </p>
                        <div className="form-group">
                          <label className="form-label">Nombre</label>
                          <input
                            type="text"
                            className="form-input"
                            value={c.nombre}
                            onChange={(e) => updateCompanion(i, 'nombre', e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Apellidos</label>
                          <input
                            type="text"
                            className="form-input"
                            value={c.apellidos}
                            onChange={(e) => updateCompanion(i, 'apellidos', e.target.value)}
                          />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label className="form-label">Email</label>
                          <input
                            type="email"
                            className="form-input"
                            value={c.email}
                            onChange={(e) => updateCompanion(i, 'email', e.target.value)}
                          />
                        </div>
                      </div>
                    ))}

                    {companions.length < 2 && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setCompanions((cs) => [...cs, emptyCompanion()])}
                        style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      >
                        <Plus size={16} /> Añadir acompañante
                      </button>
                    )}
                  </div>
                </div>
              )}

              {error && <div className="alert alert-error">{error}</div>}

              <button type="submit" className="btn btn-primary w-full" disabled={loading}>
                {loading ? 'Enviando...' : 'Confirmar inscripción'}
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default GalaRegistration;
